import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { BRAND_KIT_ROLES, BrandKit, HEX_COLOR_RE } from './types';
import { fieldSx, vb } from './theme';
import { MOBILE_SX } from '../../mobile/mobileApp';

// Four colour rows with a swatch, a native colour picker and a hex field.
// Shared by the Organization Settings card and the explainer wizard.
const BrandKitEditor: React.FC<{ value: BrandKit; onChange: (next: BrandKit) => void; compact?: boolean }> = ({
  value,
  onChange,
  compact = false
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: compact ? '1fr 1fr' : '1fr',
      gap: compact ? 1.5 : 1.25,
      // A phone: one color to a row; two side by side don't fit their hex fields.
      [MOBILE_SX]: { gridTemplateColumns: 'minmax(0, 1fr)' }
    }}
  >
    {BRAND_KIT_ROLES.map((role) => {
      const v = value[role.key];
      const valid = HEX_COLOR_RE.test(v);
      return (
        <Box
          key={role.key}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.25,
            borderRadius: '10px',
            border: `1px solid ${vb.border}`,
            background: vb.page
          }}
        >
          <Box
            component="label"
            aria-label={`${role.label} color`}
            sx={{
              width: 44,
              height: 44,
              borderRadius: '10px',
              flexShrink: 0,
              background: valid ? v : '#333',
              border: `1px solid ${vb.borderStrong}`,
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <input
              type="color"
              value={valid ? v.toUpperCase() : '#000000'}
              onChange={(e) => onChange({ ...value, [role.key]: e.target.value.toUpperCase() })}
              style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: vb.font, fontSize: 13, fontWeight: 700, color: vb.text }}>
              {role.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: vb.font,
                fontSize: 11.5,
                color: vb.textTertiary,
                [MOBILE_SX]: { whiteSpace: 'normal' }
              }}
              noWrap
            >
              {role.hint}
            </Typography>
          </Box>
          <TextField
            size="small"
            value={v}
            onChange={(e) => onChange({ ...value, [role.key]: e.target.value.trim() })}
            error={!valid}
            inputProps={{
              'aria-label': `${role.label} hex value`,
              maxLength: 7,
              style: { fontFamily: 'monospace', width: 84 }
            }}
            sx={fieldSx}
          />
        </Box>
      );
    })}
  </Box>
);

export default BrandKitEditor;

export const isValidBrandKit = (kit: BrandKit): boolean => BRAND_KIT_ROLES.every((r) => HEX_COLOR_RE.test(kit[r.key]));

// Contrast check (§62): warn when text on background is hard to read.
const luminance = (hex: string): number => {
  const c = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4]
    .map((i) => parseInt(c.slice(i, i + 2), 16) / 255)
    .map((x) => (x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export const contrastRatio = (a: string, b: string): number => {
  if (!HEX_COLOR_RE.test(a) || !HEX_COLOR_RE.test(b)) return 21;
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
};
